import type { OrgVendor } from '../../../../hooks/useGetSettings';
import { resolveLocationList } from '../../../../components/settings/locationListValue';

/** Either every location, or an explicit list of location ids. */
export type LocationValue = Array<string> | 'all';

export type VendorPreferenceKey =
    | 'required'
    | 'superPreferred'
    | 'preferred'
    | 'avoid'
    | 'prohibited';

export type VendorPreferences = Record<VendorPreferenceKey, LocationValue>;

export const VENDOR_PREFERENCE_COLUMNS: Array<{ key: VendorPreferenceKey; label: string }> = [
    { key: 'required', label: 'Required' },
    { key: 'superPreferred', label: 'Super Preferred' },
    { key: 'preferred', label: 'Preferred' },
    { key: 'avoid', label: 'Avoided' },
    { key: 'prohibited', label: 'Prohibited' },
];

type VendorLocationList = Array<{ _id: string; title: string }> | 'all';

/**
 * Stored preferences carry the ids they were saved with, which a recreated
 * location no longer has. Resolving against the org's locations first means the
 * matrix ticks every shop the vendor actually holds, matching the counts the
 * vendor table shows.
 */
const toIdArray = (
    stored: VendorLocationList,
    locations: Array<{ _id: string | 'all'; title: string }>,
): LocationValue => {
    const resolved = resolveLocationList(stored, locations);
    return resolved === 'all' ? 'all' : resolved.map((location) => location._id);
};

export const buildVendorPreferences = (
    vendor: OrgVendor | undefined,
    locations: Array<{ _id: string | 'all'; title: string }>,
): VendorPreferences => ({
    required: vendor ? toIdArray(vendor.requiredLocations, locations) : [],
    superPreferred: vendor ? toIdArray(vendor.superPreferredLocations, locations) : [],
    preferred: vendor ? toIdArray(vendor.preferredLocations, locations) : [],
    avoid: vendor ? toIdArray(vendor.avoidLocations, locations) : [],
    prohibited: vendor ? toIdArray(vendor.prohibitedLocations, locations) : [],
});

/** Shape the API stores: the location objects, or the 'all' sentinel. */
export const toLocationPayload = (
    value: LocationValue,
    locations: Array<{ _id: string; title: string }>,
): VendorLocationList => {
    if (value === 'all') return 'all';
    return value
        .map((id) => locations.find((location) => location._id === id))
        .filter((location): location is { _id: string; title: string } => !!location)
        .map((location) => ({ _id: location._id, title: location.title }));
};

export const isLocationChecked = (value: LocationValue, locationId: string): boolean =>
    value === 'all' || value.includes(locationId);

export const isAllChecked = (value: LocationValue): boolean => value === 'all';

/**
 * Drop one location from a category. An 'all' category becomes the explicit
 * list of every other location, so unchecking one box reads the way it looks
 * rather than clearing the whole column.
 */
const withoutLocation = (
    value: LocationValue,
    locationId: string,
    allLocationIds: string[],
): LocationValue =>
    value === 'all'
        ? allLocationIds.filter((id) => id !== locationId)
        : value.filter((id) => id !== locationId);

/**
 * Check or uncheck one location in one category. A location can only belong to
 * one category, so checking it here removes it from the other four.
 */
export const setLocationPreference = (
    preferences: VendorPreferences,
    key: VendorPreferenceKey,
    locationId: string,
    checked: boolean,
    allLocationIds: string[],
): VendorPreferences => {
    if (!checked) {
        return {
            ...preferences,
            [key]: withoutLocation(preferences[key], locationId, allLocationIds),
        };
    }

    return VENDOR_PREFERENCE_COLUMNS.reduce((next, column) => {
        const current = preferences[column.key];
        if (column.key === key) {
            next[column.key] =
                current === 'all' ? 'all' : Array.from(new Set([...current, locationId]));
        } else {
            next[column.key] = withoutLocation(current, locationId, allLocationIds);
        }
        return next;
    }, {} as VendorPreferences);
};

/** The header row: give a category every location, or none, clearing the rest. */
export const setAllPreference = (
    preferences: VendorPreferences,
    key: VendorPreferenceKey,
    enabled: boolean,
): VendorPreferences => {
    if (!enabled) return { ...preferences, [key]: [] };
    return VENDOR_PREFERENCE_COLUMNS.reduce((next, column) => {
        next[column.key] = column.key === key ? 'all' : [];
        return next;
    }, {} as VendorPreferences);
};

const sameLocationValue = (a: LocationValue, b: LocationValue): boolean => {
    if (a === 'all' || b === 'all') return a === b;
    if (a.length !== b.length) return false;
    const other = new Set(b);
    return a.every((id) => other.has(id));
};

export const samePreferences = (a: VendorPreferences, b: VendorPreferences): boolean =>
    VENDOR_PREFERENCE_COLUMNS.every((column) =>
        sameLocationValue(a[column.key], b[column.key]),
    );
